import type { AiFieldConfig } from '@databases/types/database';

export type AiFieldCellRequestTarget = {
  contentId: string;
  aiFieldType: AiFieldConfig['aiFieldType'];
  fieldId: string;
  entryId: string;
  isTargetValueEmpty: boolean;
};

export type AiFieldCellRequestState =
  | { status: 'idle' }
  | { status: 'queued' }
  | { status: 'running' };

export type EnqueueAiFieldCellRequestResult =
  | { kind: 'already-pending' }
  | { kind: 'enqueued'; completion: Promise<void> };

export type PendingAiFieldCellRequest = {
  target: AiFieldCellRequestTarget;
  status: Exclude<AiFieldCellRequestState['status'], 'idle'>;
};

type AiFieldCellRequest = {
  batchKey: string;
  key: string;
  target: AiFieldCellRequestTarget;
  run: (targets: readonly AiFieldCellRequestTarget[]) => Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
};

type AiFieldCellRequestListener = () => void;
type AiFieldCellRequestOutcome =
  | { kind: 'completed' }
  | { kind: 'failed'; error: unknown };

const IDLE_REQUEST_STATE: AiFieldCellRequestState = { status: 'idle' };

const getRequestKey = ({
  contentId,
  aiFieldType,
  fieldId,
  entryId
}: AiFieldCellRequestTarget): string =>
  JSON.stringify([contentId, aiFieldType, fieldId, entryId]);

/**
 * Coordinates AI field cell requests within the current browser. It reserves cells
 * synchronously to prevent duplicate requests and processes batches in FIFO order. The
 * first request starts immediately; compatible requests queued while it runs are combined
 * into the next stream. State subscriptions remain scoped to an individual cell.
 */
export class AiFieldCellRequestCoordinator {
  private readonly listenersByRequestKey = new Map<
    string,
    Set<AiFieldCellRequestListener>
  >();
  private readonly requestStates = new Map<string, AiFieldCellRequestState>();
  private readonly targetsByRequestKey = new Map<string, AiFieldCellRequestTarget>();
  private readonly queue: AiFieldCellRequest[] = [];
  private readonly waitUntilReadyToRun: (
    target: AiFieldCellRequestTarget
  ) => Promise<void>;
  private readonly onPendingRequestsChange: (
    requests: readonly PendingAiFieldCellRequest[]
  ) => void;
  private isRunning = false;

  public constructor(
    waitUntilReadyToRun: (target: AiFieldCellRequestTarget) => Promise<void> = () =>
      Promise.resolve(),
    onPendingRequestsChange: (
      requests: readonly PendingAiFieldCellRequest[]
    ) => void = () => undefined
  ) {
    this.waitUntilReadyToRun = waitUntilReadyToRun;
    this.onPendingRequestsChange = onPendingRequestsChange;
  }

  public subscribe(
    target: AiFieldCellRequestTarget,
    listener: AiFieldCellRequestListener
  ): () => void {
    const key = getRequestKey(target);
    const listeners = this.listenersByRequestKey.get(key) ?? new Set();
    listeners.add(listener);
    this.listenersByRequestKey.set(key, listeners);

    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        this.listenersByRequestKey.delete(key);
      }
    };
  }

  public getRequestState(target: AiFieldCellRequestTarget): AiFieldCellRequestState {
    return this.requestStates.get(getRequestKey(target)) ?? IDLE_REQUEST_STATE;
  }

  public enqueue({
    target,
    run,
    batchKey = getRequestKey(target)
  }: {
    target: AiFieldCellRequestTarget;
    run: (targets: readonly AiFieldCellRequestTarget[]) => Promise<void>;
    batchKey?: string;
  }): EnqueueAiFieldCellRequestResult {
    const key = getRequestKey(target);
    if (this.requestStates.has(key)) {
      return { kind: 'already-pending' };
    }

    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const completion = new Promise<void>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });

    this.requestStates.set(key, { status: 'queued' });
    this.targetsByRequestKey.set(key, target);
    this.queue.push({ batchKey, key, target, run, resolve, reject });
    this.notify(key);
    this.notifyPendingRequestsChange();
    this.runNextRequest();

    return { kind: 'enqueued', completion };
  }

  private runNextRequest(): void {
    if (this.isRunning) {
      return;
    }

    const firstRequest = this.queue.shift();
    if (!firstRequest) {
      return;
    }

    const requests = [firstRequest];
    while (this.queue[0]?.batchKey === firstRequest.batchKey) {
      requests.push(this.queue.shift() as AiFieldCellRequest);
    }

    this.isRunning = true;
    void this.runRequests(requests);
  }

  private async runRequests(requests: readonly AiFieldCellRequest[]): Promise<void> {
    try {
      for (const request of requests) {
        await this.waitUntilReadyToRun(request.target);
      }
      for (const request of requests) {
        this.requestStates.set(request.key, { status: 'running' });
        this.notify(request.key);
      }
      this.notifyPendingRequestsChange();
      await requests[0].run(requests.map(({ target }) => target));
    } catch (error: unknown) {
      this.completeRequests(requests, { kind: 'failed', error });
      return;
    }

    this.completeRequests(requests, { kind: 'completed' });
  }

  private completeRequests(
    requests: readonly AiFieldCellRequest[],
    outcome: AiFieldCellRequestOutcome
  ): void {
    for (const request of requests) {
      this.requestStates.delete(request.key);
      this.targetsByRequestKey.delete(request.key);
      this.notify(request.key);
    }
    this.isRunning = false;
    this.notifyPendingRequestsChange();
    this.runNextRequest();

    for (const request of requests) {
      switch (outcome.kind) {
        case 'completed':
          request.resolve();
          break;
        case 'failed':
          request.reject(outcome.error);
          break;
        default: {
          const unhandledOutcome: never = outcome;
          throw new Error(`Unhandled AI field request outcome: ${unhandledOutcome}`);
        }
      }
    }
  }

  private notify(key: string): void {
    this.listenersByRequestKey.get(key)?.forEach((listener) => listener());
  }

  private notifyPendingRequestsChange(): void {
    this.onPendingRequestsChange(
      [...this.requestStates.entries()].map(([key, state]) => {
        const target = this.targetsByRequestKey.get(key);
        if (target && state.status !== 'idle') {
          return { target, status: state.status };
        }

        throw new Error('Pending AI field request target not found');
      })
    );
  }
}
